import { Elements } from '@/components/elements';
import clsx from 'clsx';
import Image, { type StaticImageData } from 'next/image';

const HERO_IMAGE_HEIGHT_CLASS =
    'h-[280px] md:h-[320px] lg:h-[470px]';

const HERO_TITLE_CLASSNAME =
    'text-3xl font-semibold text-brand-700 md:text-5xl! lg:text-6xl!';

export type UseCasesOverviewHeroProps = {
    tag: string;
    title: string;
    /** Second line of the H1, rendered below `title` when set. */
    titleLine2?: string;
    description: string;
    secondaryDescription?: string;
    image: StaticImageData | string;
    imageAlt: string;
    className?: string;
    imagePriority?: boolean;
};

export function UseCasesOverviewHero({
    tag,
    title,
    titleLine2,
    description,
    secondaryDescription,
    image,
    imageAlt,
    className,
    imagePriority = false,
}: UseCasesOverviewHeroProps) {
    return (
        <section
            className={clsx(
                'overflow-hidden px-5 pt-20 pb-16 md:px-10 md:pb-20 lg:px-[79px]',
                className,
            )}
        >
            <div className="mx-auto grid w-full max-w-[1280px] grid-cols-1 items-stretch gap-8 lg:grid-cols-2 lg:gap-12">
                <div
                    className={clsx(
                        'relative order-2 w-full shrink-0 justify-self-stretch overflow-hidden rounded-3xl',
                        'lg:order-1 lg:max-w-none lg:justify-self-start',
                        HERO_IMAGE_HEIGHT_CLASS,
                    )}
                >
                    <Image
                        src={image}
                        alt={imageAlt}
                        fill
                        priority={imagePriority}
                        className="object-cover"
                        sizes="(min-width: 1024px) 50vw, 100vw"
                    />
                </div>
                <div
                    className={clsx(
                        'order-1 flex flex-col rounded-3xl bg-brand-100 p-6 md:p-8 lg:order-2 lg:p-10',
                        'h-auto lg:h-[470px]',
                    )}
                >
                    <span
                        className={clsx(
                            'mb-4 inline-flex w-fit rounded-lg bg-gray-50 px-2 py-1 text-base font-medium text-brand-700',
                        )}
                    >
                        {tag}
                    </span>
                    {titleLine2 ? (
                        <h1
                            className={clsx(
                                'm-0 font-semibold leading-tight',
                                HERO_TITLE_CLASSNAME,
                            )}
                        >
                            <span className="block">{title}</span>
                            <span className="mt-1 block md:mt-1.5 lg:mt-2">
                                {titleLine2}
                            </span>
                        </h1>
                    ) : (
                        <Elements.Title
                            title={title}
                            type="h1"
                            className={HERO_TITLE_CLASSNAME}
                        />
                    )}
                    <div
                        className="hidden min-h-2 flex-1 lg:block"
                        aria-hidden
                    />
                    <div className="flex flex-col gap-2">
                        <Elements.Description
                            description={description}
                            type="medium"
                            className="text-brand-600 text-sm! md:text-base! lg:text-lg!"
                        />
                        {secondaryDescription ? (
                            <Elements.Description
                                description={secondaryDescription}
                                type="medium"
                                className="text-brand-600 text-sm! md:text-base! lg:text-lg!"
                            />
                        ) : null}
                    </div>
                </div>
            </div>
        </section>
    );
}
